<template>
  <nav :class="`theme--${currentThemeAssets?.chrome ?? 'light'} ${bottomMargin ? 'mb-12' : ''}`">
    <div id="top-nav">
      <MContentLayout size="lg" class="flex h-full flex-nowrap items-stretch justify-between gap-6">
        <div
          v-if="hasSupportLinks"
          class="flex min-w-0 flex-wrap items-center justify-between divide-x divide-gray-300 text-onChrome"
          :class="{ 'mx-auto': topRowHasLinks }">
          <div v-if="$te('header_mainSiteLinkPrefix') && $te('header_mainSiteLinkText') && $te('header_mainSiteLink')" class="px-2">
            {{ $t("header_mainSiteLinkPrefix") }}
            <a :href="$t('header_mainSiteLink')" target="_blank">{{ $t("header_mainSiteLinkText") }}</a>
          </div>
          <div v-if="$te('header_callSupport') && $te('support_phone')" class="px-2">
            {{ $t("header_callSupport") }} <a :href="`tel:${$t('support_phone')}`">{{ $t("support_phone") }}</a>
          </div>
          <div v-if="$te('header_emailSupport') && $te('support_email')" class="px-2">
            {{ $t("header_emailSupport") }} <a :href="`mailto:${$t('support_email')}`">{{ $t("support_email") }}</a>
          </div>
        </div>

        <div v-if="topRowHasLinks" id="top-link-row" class="flex items-center" :class="{ grow: !hasSupportLinks }">
          <template v-for="link in topRowLinks" :key="`top-link-${link.name}`">
            <div v-if="link.growBefore && !hasSupportLinks" class="grow"></div>
            <div class="header-link">
              <HeaderLinkButton :link="link" />
            </div>
          </template>
        </div>

        <div v-if="isUserLoggedIn" class="flex h-full shrink-0 items-center" :class="{ 'ml-auto': headerHasLinks }">
          <BankingContextSelector v-if="internetBankingEnabled" />
          <button class="flex h-full shrink-0 items-center bg-border-default px-8 font-semibold hover:bg-[#e5e5e5]" @click="signOut">
            {{ $t("authentication_signOutButton") }}
          </button>
        </div>
      </MContentLayout>
    </div>

    <div id="bottom-nav">
      <MContentLayout size="lg">
        <div id="bottom-nav-inner">
          <router-link :to="{ name: routeNames.HOME }" aria-label="home" class="mb-auto mr-6">
            <img
              class="sentry-unmask h-11 max-w-40 object-contain"
              alt="logo"
              :src="currentThemeAssets?.chrome === 'dark' ? currentThemeAssets?.logoOnDark : currentThemeAssets?.logo" />
          </router-link>

          <!-- Header links displayed on desktop -->
          <div v-if="headerHasLinks" id="header-link-row">
            <template v-for="link in headerLinks" :key="`main-link-${link.name}`">
              <div v-if="link.growBefore" class="grow"></div>
              <div class="header-link">
                <HeaderLinkButton :link="link" />
              </div>
            </template>

            <div class="ml-auto flex w-fit flex-wrap justify-start gap-3">
              <MPrimaryButton
                :title="$t('navigation_paymentButton')"
                aria-controls="payment-menu"
                aria-haspopup="true"
                @click="(event: Event) => paymentMenu!.show(event)">
                <template #prefix>
                  <i class="pi pi-arrow-right-arrow-left ms-2 h-4 w-4" />
                </template>
                <template #postfix>
                  <MDropDownIcon class="me-2 h-3 w-3" />
                </template>
              </MPrimaryButton>
              <Menu id="payment-menu" ref="paymentMenu" :model="paymentLinks" :popup="true" />

              <MSecondaryButton
                :title="$t('accounts_openAccountButton')"
                aria-controls="account-menu"
                aria-haspopup="true"
                :aria-label="$t('accounts_openAccountButton')"
                @click="(event: Event) => accountMenu!.show(event)">
                <template #prefix>
                  <MAddIcon class="h-6 w-6" />
                </template>
                <template #postfix>
                  <MDropDownIcon class="me-2 h-3 w-3" />
                </template>
              </MSecondaryButton>
              <Menu id="account-menu" ref="accountMenu" :model="accountLinks" :popup="true" />
            </div>
          </div>

          <!-- Header links displayed on mobile devices -->
          <div
            v-if="mobileHasLinks"
            id="mobile-nav-container"
            :class="isMenuOpen ? 'bg-black/80 lg:bg-transparent' : 'hidden lg:block'"
            :style="{ left: isMenuOpen ? 0 : undefined }"
            @click="closeMenu">
            <div id="mobile-nav-panel">
              <button aria-label="menu" @click="closeMenu">
                <MCloseMenuIcon id="close-icon" :svg-color="colors.on.secondary" />
              </button>

              <div class="border-t border-t-white"></div>
              <div v-for="subLink in mobileLinks" :key="`sub-link-${subLink?.name}`" class="nav-link">
                <button v-if="linkOnlyHasAction(subLink)" @click="() => subLink.action?.()">
                  <div class="header-mobile-link-text">{{ subLink.name }}</div>
                </button>
                <a v-else-if="isUrlLink(subLink)" :href="(subLink as HeaderUrlLink).url" target="_blank">
                  <div class="header-mobile-link-text">{{ subLink.name }}</div>
                </a>
                <router-link v-else :to="getRouteForLink(subLink as HeaderRouteLink)" @click="subLink.action">
                  <div class="header-mobile-link-text">{{ subLink.name }}</div>
                </router-link>
              </div>
            </div>
          </div>

          <button id="menu-button" aria-label="menu" @click="openMenu">
            <MMenuIcon v-if="Object.keys(mobileLinks).length > 0" id="menu-icon" />
          </button>
        </div>
      </MContentLayout>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from "vue";
import { routeNames } from "@/router/constants";
import {
  MAddIcon,
  MCloseMenuIcon,
  MContentLayout,
  MDropDownIcon,
  MMenuIcon,
  MPrimaryButton,
  MSecondaryButton,
  colors,
  useIntl,
  useTheme,
} from "@moroku/money-webapp-components";
import { getRouteForLink, type HeaderRouteLink, type HeaderUrlLink, isUrlLink, linkOnlyHasAction } from "@/types/headerLinks";
import useHeaderLinks from "@/composables/useHeaderLinks";
import HeaderLinkButton from "@/components/HeaderLinkButton.vue";
import BankingContextSelector from "@/components/BankingContextSelector.vue";
import useAuthUser from "@/composables/useAuthUser.ts";
import Menu from "primevue/menu";
import { useRouter } from "vue-router";
import { linkExternalAccount } from "@/api/openBankingApi";
import { useAccounts } from "@/composables/useAccounts";
import { featureEnabled } from "@/config";
import { MoneyFeatures } from "@/config/constants.ts";

const isMenuOpen = ref(false);
const { currentThemeAssets } = useTheme();
const { te } = useIntl();
const paymentMenu = useTemplateRef("paymentMenu");
const accountMenu = useTemplateRef("accountMenu");

const emit = defineEmits(["externalAccountLinked"]);

const openMenu = () => {
  isMenuOpen.value = true;
  document.documentElement.style.overflow = "hidden";
};
const closeMenu = () => {
  isMenuOpen.value = false;
  document.documentElement.style.overflow = "auto";
};

onMounted(() => {
  window.addEventListener("resize", closeMenu);
});
onUnmounted(() => {
  window.removeEventListener("resize", closeMenu);
  removeVisibilityListener();
});

const { t } = useIntl();
const router = useRouter();
const { headerLinks, mobileLinks, topRowLinks, paymentLinks } = useHeaderLinks();
const { authUser, signOut } = useAuthUser();
const isUserLoggedIn = computed(() => !!authUser.value);
const internetBankingEnabled = featureEnabled(MoneyFeatures.InternetBanking);

const headerHasLinks = computed(() => Object.keys(headerLinks.value).length > 0);
const mobileHasLinks = computed(() => Object.keys(mobileLinks.value).length > 0);
const topRowHasLinks = computed(() => Object.keys(topRowLinks.value).length > 0);
const hasSupportLinks = computed(() => te("header_callSupport") || te("header_emailSupport") || te("header_mainSiteLink"));

const bottomMargin = computed(() => router.currentRoute.value.meta.headerBottomMargin ?? true);

const requestLinkExternalAccount = async () => {
  const res = await linkExternalAccount();
  const consentUrl = res.consentUrl;
  window.open(consentUrl!, "_blank", "noreferrer");

  addEventListener(visibilityEvent, detectUserReturn);
};

const detectUserReturn = async () => {
  if (document.visibilityState !== "visible") {
    return;
  }

  removeEventListener(visibilityEvent, detectUserReturn);

  const numberOfAccountsBefore = accounts.value?.length ?? 0;
  await refetch();
  const numberOfAccountsAfter = accounts.value?.length ?? 0;

  if (numberOfAccountsAfter > numberOfAccountsBefore) {
    emit("externalAccountLinked");
  }
};

const accountLinks = [
  {
    label: t("accounts_openAccountButton"),
    command: async () => {
      await router.push({ name: routeNames.OPEN_ACCOUNT });
    },
  },
  {
    label: t("accounts_linkExternalButton"),
    command: requestLinkExternalAccount,
  },
];

const visibilityEvent = "visibilitychange";

const { refetch, accounts } = useAccounts();

const removeVisibilityListener = () => addEventListener(visibilityEvent, detectUserReturn);
</script>

<style lang="scss" scoped>
.theme--light {
  --background-color: var(--color-chrome);
  --text-color: var(--color-onChrome);
  --hover-color: var(--color-chromeSecondary);
  --active-color: var(--color-chromeTertiary);
}

.theme--dark {
  --background-color: var(--color-chrome);
  --text-color: var(--color-onChrome);
  --hover-color: var(--color-onChrome);
  --hover-bg: var(--color-chromeSecondary);
  --active-bg: var(--color-chromeTertiary);
}

#top-nav {
  @apply border-0 border-b border-border-default bg-surface-light shadow;
  height: 3.5rem;
}

#bottom-nav {
  @apply bg-surface py-2 shadow;
}

#bottom-nav-inner {
  @apply flex w-full items-center gap-2 py-3;
}

#top-link-row {
  @apply flex flex-row items-center gap-6;
}

#header-link-row {
  @apply hidden grow flex-row flex-wrap items-center gap-6 lg:flex;

  a {
    color: var(--text-color);
  }
}

.header-link #header-link-row:not(:hover) {
  .header-link > .router-link-exact-active > :deep(.header-link-text) {
    color: var(--active-color);
    background-color: var(--active-bg);
  }
}

#menu-button {
  @apply my-auto ml-auto block h-fit cursor-pointer rounded-full transition-all hover:shadow-xl lg:hidden;

  &:hover {
    color: var(--hover-color);
    background-color: var(--hover-bg);
  }
}

#menu-icon {
  svg {
    @apply fill-transparent;
  }
}

#close-icon {
  @apply text-on-secondary hover:bg-on-secondary hover:text-secondary;
  @apply mb-4 ml-auto mr-4 block w-8 cursor-pointer rounded-full transition-all hover:shadow-xl lg:hidden;

  svg {
    @apply fill-none;
  }
}

#mobile-nav-container {
  @apply absolute inset-y-0 right-0 z-50 font-semibold lg:hidden;

  #mobile-nav-panel {
    @apply absolute inset-y-0 right-0 flex h-screen min-w-60 flex-col bg-secondary py-4 lg:static lg:bg-transparent;

    .nav-link {
      @apply border-b border-b-white hover:bg-primary focus:outline-none lg:p-0;
    }

    .header-mobile-link-text {
      @apply cursor-pointer p-4 text-on-secondary;
    }
  }

  #mobile-nav-panel:not(:hover) {
    overflow: auto;

    .nav-link > .router-link-exact-active > .header-mobile-link-text {
      @apply bg-primary;
    }
  }
}
</style>
